import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/utils/cn";
import { TaskCapabilities } from "../taskCapabilities";
import { Task } from "../types";
import { TaskAssigneePill, TaskCheckButton } from "./TaskRowParts";

const ClientEntityIcon = ENTITY_CONFIG.client.icon;

interface TaskRowProps {
  task: Task;
  capabilities: TaskCapabilities;
  onToggle: (task: Task) => void;
  /** Opens the list's task drawer (DD-017). */
  onOpen?: (task: Task) => void;
}

/**
 * One task row on every surface (DD-017): check circle, title, clamped
 * description, for team viewers a client icon when the client sees the task,
 * and the owner pill. The check circle is the row's one control, a
 * one-tap check-off that never opens the drawer. A click anywhere else on
 * the row opens it. For keyboards the title is the open button (Enter), so
 * no button role wraps another button.
 */
export function TaskRow({ task, capabilities, onToggle, onOpen }: TaskRowProps) {
  const { t } = useLocale();
  const done = task.status === "done";
  const open = onOpen ? () => onOpen(task) : undefined;

  const text = (
    <>
      <span className={cn("block text-sm truncate", done ? "text-gray-400 line-through" : "text-gray-800")}>
        {task.title}
      </span>
      {task.description && (
        // A span, not a p: the text sits inside the open button.
        <span className={cn("text-xs line-clamp-2", done ? "text-gray-300" : "text-gray-500")}>
          {task.description}
        </span>
      )}
    </>
  );

  return (
    <div
      onClick={open}
      className={cn(
        "flex items-center gap-3 py-2.5",
        open && "px-1 cursor-pointer transition-colors hover:bg-gray-50/60"
      )}
    >
      <TaskCheckButton task={task} toggleable={capabilities.canToggle(task)} onToggle={onToggle} />
      {open ? (
        <button
          type="button"
          onClick={(event) => {
            // The row's own onClick would open it a second time.
            event.stopPropagation();
            open();
          }}
          className="flex-1 min-w-0 text-left rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {text}
        </button>
      ) : (
        <div className="flex-1 min-w-0">{text}</div>
      )}
      {capabilities.canSeeVisibility && task.clientVisible && (
        // The team sees which rows the client sees (DD-017, DD-053).
        <span title={t("tasks.visibleToClient")} aria-label={t("tasks.visibleToClient")} className="shrink-0 inline-flex text-slate-400">
          <ClientEntityIcon className="w-3.5 h-3.5" />
        </span>
      )}
      <TaskAssigneePill assignee={task.assignee} owner />
    </div>
  );
}
